import { useState } from "react";
import { CalendarDaysIcon, SparklesIcon } from "@heroicons/react/24/outline";

interface EmptyPlanStateProps {
  onCreatePlan: (name: string) => Promise<void>;
  isLoading?: boolean;
}

export function EmptyPlanState({ onCreatePlan, isLoading }: EmptyPlanStateProps) {
  const [planName, setPlanName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!planName.trim()) return;
    setIsCreating(true);
    try {
      await onCreatePlan(planName.trim());
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="max-w-md">
        <div className="flex items-center justify-center gap-2 mb-4">
          <CalendarDaysIcon className="w-12 h-12 text-gray-300" />
          <SparklesIcon className="w-8 h-8 text-brand-400" />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Create Your Training Plan
        </h2>
        <p className="text-gray-500 mb-6">
          Your AI running coach will analyze your recent training and help you
          build a personalized plan based on your goals.
        </p>

        <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            What you'll get:
          </h3>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-brand-500 mt-0.5">•</span>
              Personalized training schedule based on your history
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500 mt-0.5">•</span>
              Smart activity matching to track your progress
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500 mt-0.5">•</span>
              Conversational coach to adjust the plan as you go
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            placeholder="Give your plan a name (e.g., Spring Half Marathon)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
            disabled={isLoading || isCreating}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={isLoading || isCreating || !planName.trim()}
            className="w-full px-4 py-2.5 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isCreating ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Creating...
              </>
            ) : (
              <>
                <SparklesIcon className="w-5 h-5" />
                Create Plan & Start Coaching
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
